import { ArrowLeft, Bell, Check, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Avatar, AvatarFallback } from '@apps/web/components/ui/avatar';
import { Button } from '@apps/web/components/ui/button';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    CommandSeparator,
} from '@apps/web/components/ui/command';
import { Label } from '@apps/web/components/ui/label';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@apps/web/components/ui/popover';
import { TableCell, TableRow } from '@apps/web/components/ui/table';
import {
    getAvatarInitials,
    getAvatarToneClass,
} from '@apps/web/components/ui/user_avatar';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import { cn } from '@apps/web/lib/utils';
import {
    useOrganizationUsers,
    useOutreachSubscribers,
    useUser,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';

type OutreachSubscribersPanelProps = {
    readonly displayMode?: 'banner' | 'compact' | 'detailRow';
    readonly organizationId: string;
    readonly outreachId: string;
};

type SubscriberPerson = {
    readonly email: string | null;
    readonly first_name: string | null;
    readonly id: string;
    readonly last_name: string | null;
};

const MAX_VISIBLE_SUBSCRIBER_BADGES = 5;
const MAX_VISIBLE_AVATARS_WITH_OVERFLOW = MAX_VISIBLE_SUBSCRIBER_BADGES - 1;

export function OutreachSubscribersPanel({
    displayMode = 'banner',
    organizationId,
    outreachId,
}: OutreachSubscribersPanelProps) {
    const { data, mutate, isLoading } = useOutreachSubscribers(
        organizationId,
        outreachId,
        'outreach_detail.subscribers_panel',
    );
    const { data: organizationUsers, isLoading: isLoadingOrganizationUsers } =
        useOrganizationUsers(organizationId, {
            reason: 'outreach_detail.subscribers_panel.organization_users',
        });
    const { data: currentUser } = useUser({
        reason: 'outreach_detail.subscribers_panel.current_user',
    });
    const [busy, setBusy] = useState(false);
    const [subscriberPopoverOpen, setSubscriberPopoverOpen] = useState(false);
    const [subscriberSearch, setSubscriberSearch] = useState('');
    const sortedSubscribers = useMemo(
        () =>
            [...(data?.subscribers ?? [])].sort((left, right) =>
                formatSubscriberLabel(left).localeCompare(
                    formatSubscriberLabel(right),
                ),
            ),
        [data?.subscribers],
    );
    const currentSubscriberIds = useMemo(
        () => new Set(sortedSubscribers.map((subscriber) => subscriber.id)),
        [sortedSubscribers],
    );
    const mergedUsers = useMemo(() => {
        const usersById = new Map<string, SubscriberPerson>();

        for (const user of organizationUsers ?? []) {
            // A null id means a pending invitation with no account yet. Status
            // reports whether a member has signed in, which does not affect
            // whether they can be subscribed to outreach.
            if (user.id === null) {
                continue;
            }

            usersById.set(user.id, {
                email: user.email,
                first_name: user.first_name,
                id: user.id,
                last_name: user.last_name,
            });
        }

        for (const subscriber of sortedSubscribers) {
            usersById.set(subscriber.id, {
                email: subscriber.email,
                first_name: subscriber.first_name,
                id: subscriber.id,
                last_name: subscriber.last_name,
            });
        }

        return [...usersById.values()];
    }, [organizationUsers, sortedSubscribers]);
    const filteredUsers = useMemo(() => {
        const normalizedSearch = subscriberSearch.trim().toLowerCase();

        return mergedUsers
            .filter((user) => {
                if (normalizedSearch.length === 0) {
                    return true;
                }

                return `${formatSubscriberLabel(user)} ${user.email ?? ''}`
                    .toLowerCase()
                    .includes(normalizedSearch);
            })
            .sort((left, right) => {
                const leftSubscribed = currentSubscriberIds.has(left.id)
                    ? 0
                    : 1;
                const rightSubscribed = currentSubscriberIds.has(right.id)
                    ? 0
                    : 1;
                if (leftSubscribed !== rightSubscribed) {
                    return leftSubscribed - rightSubscribed;
                }

                return formatSubscriberLabel(left).localeCompare(
                    formatSubscriberLabel(right),
                );
            });
    }, [currentSubscriberIds, mergedUsers, subscriberSearch]);
    const subscribedOptions = filteredUsers.filter((user) =>
        currentSubscriberIds.has(user.id),
    );
    const unsubscribedOptions = filteredUsers.filter(
        (user) => !currentSubscriberIds.has(user.id),
    );
    const hasUnsubscribedUsers = mergedUsers.some(
        (user) => !currentSubscriberIds.has(user.id),
    );
    const visibleSubscriberCount =
        sortedSubscribers.length > MAX_VISIBLE_SUBSCRIBER_BADGES
            ? MAX_VISIBLE_AVATARS_WITH_OVERFLOW
            : MAX_VISIBLE_SUBSCRIBER_BADGES;
    const visibleSubscribers = sortedSubscribers.slice(
        0,
        visibleSubscriberCount,
    );
    const hiddenSubscriberCount = Math.max(
        0,
        sortedSubscribers.length - visibleSubscribers.length,
    );
    const subscriberTriggerLabel =
        sortedSubscribers.length === 0
            ? 'Add subscribers'
            : 'Change subscribers';

    if (isLoading || !data || !currentUser) {
        return null;
    }

    const handleSubscriberPopoverOpenChange = (open: boolean) => {
        setSubscriberPopoverOpen(open);
        if (!open) {
            setSubscriberSearch('');
        }
    };

    const updateSubscriber = (subscriberUserId: string, subscribe: boolean) => {
        const apiClient = getApiClient();
        if (subscribe) {
            return apiClient.post({
                url: '/organizations/{organization_id}/outreaches/{outreach_id}/subscribers',
                pathParams: {
                    organization_id: organizationId,
                    outreach_id: outreachId,
                },
                body: { user_id: subscriberUserId },
            });
        }

        return apiClient.delete({
            url: '/organizations/{organization_id}/outreaches/{outreach_id}/subscribers/{subscriber_user_id}',
            pathParams: {
                organization_id: organizationId,
                outreach_id: outreachId,
                subscriber_user_id: subscriberUserId,
            },
        });
    };

    const handleSubscribeToggle = async () => {
        setBusy(true);
        try {
            if (data.current_user_is_subscribed) {
                await updateSubscriber(currentUser.id, false);
                toast.success('Unsubscribed from this outreach', {
                    persistNotification: true,
                    notificationType: 'outreach_action',
                });
            } else {
                await updateSubscriber(currentUser.id, true);
                toast.success('Subscribed to this outreach', {
                    persistNotification: true,
                    notificationType: 'outreach_action',
                });
            }
            await mutate();
        } catch (error) {
            console.error('Failed to toggle outreach subscription:', error);
            toast.error('Failed to update subscription', {
                persistNotification: true,
                notificationType: 'outreach_action',
            });
        } finally {
            setBusy(false);
        }
    };

    const handleSubscriberToggle = async (subscriberUserId: string) => {
        setBusy(true);
        try {
            if (currentSubscriberIds.has(subscriberUserId)) {
                await updateSubscriber(subscriberUserId, false);
                toast.success('Subscriber removed', {
                    persistNotification: true,
                    notificationType: 'outreach_action',
                });
            } else {
                await updateSubscriber(subscriberUserId, true);
                toast.success('Subscriber added', {
                    persistNotification: true,
                    notificationType: 'outreach_action',
                });
            }
            await mutate();
        } catch (error) {
            console.error('Failed to toggle outreach subscriber:', error);
            toast.error('Failed to update subscribers', {
                persistNotification: true,
                notificationType: 'outreach_action',
            });
        } finally {
            setBusy(false);
        }
    };

    const handleBulkSubscriberUpdate = async (subscribe: boolean) => {
        const subscriberUserIds = subscribe
            ? mergedUsers
                  .filter((user) => !currentSubscriberIds.has(user.id))
                  .map((user) => user.id)
            : sortedSubscribers.map((subscriber) => subscriber.id);
        if (subscriberUserIds.length === 0) {
            return;
        }

        setBusy(true);
        try {
            const results = await Promise.allSettled(
                subscriberUserIds.map((subscriberUserId) =>
                    updateSubscriber(subscriberUserId, subscribe),
                ),
            );
            await mutate();

            const failures = results.filter(
                (result) => result.status === 'rejected',
            );
            if (failures.length > 0) {
                console.error(
                    'Failed to update some outreach subscribers:',
                    failures,
                );
                toast.error('Some subscribers could not be updated', {
                    persistNotification: true,
                    notificationType: 'outreach_action',
                });
                return;
            }

            toast.success(
                subscribe
                    ? 'All team members selected'
                    : 'All subscribers removed',
                {
                    persistNotification: true,
                    notificationType: 'outreach_action',
                },
            );
        } catch (error) {
            console.error('Failed to update outreach subscribers:', error);
            toast.error('Failed to update subscribers', {
                persistNotification: true,
                notificationType: 'outreach_action',
            });
        } finally {
            setBusy(false);
        }
    };

    const isCompact = displayMode === 'compact';

    const renderSubscribeToggleButton = () => {
        if (data.current_user_is_subscribed) {
            return (
                <button
                    type="button"
                    aria-label="Unsubscribe"
                    className={cn(
                        'text-muted-foreground hover:text-foreground cursor-pointer transition-colors disabled:opacity-50',
                        isCompact ? 'text-xs' : 'text-sm',
                    )}
                    onClick={handleSubscribeToggle}
                    disabled={busy}
                >
                    {isCompact ? 'Unsub' : 'Unsubscribe'}
                </button>
            );
        }

        return (
            <Button
                className={cn(isCompact ? 'h-7 px-2 text-xs' : 'px-8')}
                variant="outline"
                size="sm"
                onClick={handleSubscribeToggle}
                disabled={busy}
                aria-label="Subscribe to updates"
            >
                <Bell className="h-4 w-4" />
                {isCompact ? 'Subscribe' : 'Subscribe to updates'}
            </Button>
        );
    };

    const subscriberList = (
        <div
            className={cn(
                isCompact ? 'min-w-0' : 'space-y-3',
                isCompact && 'contents',
            )}
            data-testid={isCompact ? 'outreach-subscribers-compact' : undefined}
        >
            {displayMode === 'banner' ? <Label>Subscribers</Label> : null}
            <div
                className={cn(
                    'flex items-center gap-2',
                    isCompact ? 'min-w-0 flex-nowrap' : 'flex-wrap',
                )}
            >
                <div className="shrink-0">{renderSubscribeToggleButton()}</div>
                <Popover
                    open={subscriberPopoverOpen}
                    onOpenChange={handleSubscriberPopoverOpenChange}
                >
                    <PopoverTrigger asChild={true}>
                        <button
                            type="button"
                            aria-label={subscriberTriggerLabel}
                            className="focus-visible:ring-ring inline-flex cursor-pointer items-center rounded-full transition-transform outline-none hover:scale-[1.02] focus-visible:ring-2"
                            disabled={busy}
                            title={
                                sortedSubscribers.length === 0
                                    ? subscriberTriggerLabel
                                    : sortedSubscribers
                                          .map((subscriber) =>
                                              formatSubscriberLabel(subscriber),
                                          )
                                          .join(', ')
                            }
                        >
                            <SubscriberAvatarStack
                                hiddenCount={hiddenSubscriberCount}
                                subscribers={visibleSubscribers}
                            />
                            <span className="sr-only">
                                {subscriberTriggerLabel}
                            </span>
                        </button>
                    </PopoverTrigger>
                    <PopoverContent
                        className="w-[calc(100vw-2rem)] p-0 sm:w-[360px]"
                        align="start"
                    >
                        <div className="border-b px-3 py-3">
                            <div className="flex items-center gap-2">
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 shrink-0"
                                    onClick={() =>
                                        handleSubscriberPopoverOpenChange(false)
                                    }
                                    aria-label="Back to outreach"
                                >
                                    <ArrowLeft className="h-4 w-4" />
                                </Button>
                                <div className="min-w-0">
                                    <p className="text-sm font-semibold">
                                        Subscribers
                                    </p>
                                    <p className="text-muted-foreground mt-1 text-xs">
                                        Choose who should receive updates for
                                        this outreach.{' '}
                                        <a
                                            className="underline"
                                            href="/settings?tab=user"
                                        >
                                            Manage your subscription preferences
                                            here
                                        </a>
                                        .
                                    </p>
                                </div>
                            </div>
                        </div>
                        <Command shouldFilter={false}>
                            <CommandInput
                                placeholder="Search team members…"
                                value={subscriberSearch}
                                onValueChange={setSubscriberSearch}
                                disabled={busy}
                            />
                            <div className="flex items-center justify-between gap-3 border-b px-3 py-2">
                                <span className="text-muted-foreground text-xs">
                                    {sortedSubscribers.length} selected
                                </span>
                                <div className="flex items-center gap-1">
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="h-7 px-2 text-xs"
                                        onClick={() =>
                                            void handleBulkSubscriberUpdate(
                                                true,
                                            )
                                        }
                                        disabled={
                                            busy ||
                                            isLoadingOrganizationUsers ||
                                            !hasUnsubscribedUsers
                                        }
                                    >
                                        Select all
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="h-7 px-2 text-xs"
                                        onClick={() =>
                                            void handleBulkSubscriberUpdate(
                                                false,
                                            )
                                        }
                                        disabled={
                                            busy ||
                                            sortedSubscribers.length === 0
                                        }
                                    >
                                        Deselect all
                                    </Button>
                                </div>
                            </div>
                            <CommandList className="max-h-[320px]">
                                <CommandEmpty>
                                    {isLoadingOrganizationUsers
                                        ? 'Loading…'
                                        : 'No team members found.'}
                                </CommandEmpty>
                                {subscribedOptions.length > 0 ? (
                                    <CommandGroup heading="Subscribers">
                                        {subscribedOptions.map((user) => (
                                            <SubscriberCommandItem
                                                key={user.id}
                                                busy={busy}
                                                isChecked={true}
                                                isCurrentUser={
                                                    user.id === currentUser.id
                                                }
                                                onSelect={() =>
                                                    void handleSubscriberToggle(
                                                        user.id,
                                                    )
                                                }
                                                user={user}
                                            />
                                        ))}
                                    </CommandGroup>
                                ) : null}
                                {subscribedOptions.length > 0 &&
                                unsubscribedOptions.length > 0 ? (
                                    <CommandSeparator />
                                ) : null}
                                {unsubscribedOptions.length > 0 ? (
                                    <CommandGroup heading="Team members">
                                        {unsubscribedOptions.map((user) => (
                                            <SubscriberCommandItem
                                                key={user.id}
                                                busy={busy}
                                                isChecked={false}
                                                isCurrentUser={
                                                    user.id === currentUser.id
                                                }
                                                onSelect={() =>
                                                    void handleSubscriberToggle(
                                                        user.id,
                                                    )
                                                }
                                                user={user}
                                            />
                                        ))}
                                    </CommandGroup>
                                ) : null}
                            </CommandList>
                        </Command>
                    </PopoverContent>
                </Popover>
            </div>
        </div>
    );

    if (displayMode === 'detailRow') {
        return (
            <TableRow className="border-0">
                <TableCell className="text-muted-foreground w-[140px] pl-0 align-top font-medium">
                    Subscribers
                </TableCell>
                <TableCell className="border-0">{subscriberList}</TableCell>
            </TableRow>
        );
    }

    if (isCompact) {
        return subscriberList;
    }

    return (
        <div className="bg-muted/50 rounded-md border px-4 py-3">
            {subscriberList}
        </div>
    );
}

type SubscriberAvatarStackProps = {
    readonly hiddenCount: number;
    readonly subscribers: ReadonlyArray<SubscriberPerson>;
};

function SubscriberAvatarStack({
    hiddenCount,
    subscribers,
}: SubscriberAvatarStackProps) {
    if (subscribers.length === 0) {
        return (
            <div className="border-border bg-background text-muted-foreground flex h-8 w-8 items-center justify-center rounded-full border border-dashed text-xs shadow-sm">
                <Plus className="h-4 w-4" />
            </div>
        );
    }

    return (
        <div className="flex -space-x-4" data-testid="subscriber-avatar-stack">
            {subscribers.map((subscriber) => (
                <SubscriberAvatar
                    key={subscriber.id}
                    label={formatSubscriberLabel(subscriber)}
                    sizeClassName="h-8 w-8 text-[11px]"
                />
            ))}
            {hiddenCount > 0 ? (
                <div
                    className="border-background bg-muted text-muted-foreground flex h-8 w-8 items-center justify-center rounded-full border-2 text-[11px] font-medium shadow-sm"
                    data-testid="subscriber-avatar-overflow-badge"
                >
                    +{hiddenCount}
                </div>
            ) : null}
        </div>
    );
}

type SubscriberCommandItemProps = {
    readonly busy: boolean;
    readonly isChecked: boolean;
    readonly isCurrentUser: boolean;
    readonly onSelect: () => void;
    readonly user: SubscriberPerson;
};

function SubscriberCommandItem({
    busy,
    isChecked,
    isCurrentUser,
    onSelect,
    user,
}: SubscriberCommandItemProps) {
    return (
        <CommandItem
            className="gap-3 py-2"
            disabled={busy}
            value={`${formatSubscriberLabel(user)} ${user.email ?? ''}`}
            onSelect={onSelect}
        >
            <div
                className={cn(
                    'flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition-colors',
                    isChecked
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-muted-foreground bg-background',
                )}
            >
                {isChecked ? <Check className="h-3.5 w-3.5" /> : null}
            </div>
            <SubscriberAvatar
                label={formatSubscriberLabel(user)}
                sizeClassName="h-7 w-7 text-[10px]"
            />
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                    <span className="truncate">
                        {formatSubscriberLabel(user)}
                    </span>
                    {isCurrentUser ? (
                        <span className="text-muted-foreground shrink-0 text-xs">
                            You
                        </span>
                    ) : null}
                </div>
                <div className="text-muted-foreground truncate text-xs">
                    {user.email ?? 'No email'}
                </div>
            </div>
        </CommandItem>
    );
}

type SubscriberAvatarProps = {
    readonly label: string;
    readonly sizeClassName: string;
};

function SubscriberAvatar({ label, sizeClassName }: SubscriberAvatarProps) {
    const initials = getAvatarInitials(label);
    const toneClassName = getAvatarToneClass(label);

    return (
        <Avatar
            className={cn(
                'border-background shrink-0 border-2 shadow-sm',
                sizeClassName,
            )}
            data-testid="subscriber-avatar-badge"
        >
            <AvatarFallback className={cn('font-medium', toneClassName)}>
                {initials}
            </AvatarFallback>
        </Avatar>
    );
}

function formatSubscriberLabel(subscriber: {
    readonly email: string | null;
    readonly first_name: string | null;
    readonly last_name: string | null;
}): string {
    const name =
        `${subscriber.first_name ?? ''} ${subscriber.last_name ?? ''}`.trim();
    return name.length > 0 ? name : (subscriber.email ?? 'Unknown user');
}
